import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";
import { API_BASE, resolveImageUrl } from "../../config/api";
import Pagination from "../../Components/Pagination";

const PAGE_SIZE = 25;

interface Offer {
  offer_id: number;
  store_id: number;
  offer_image: string;
  offer_image_url: string;
  title: string | null;
  is_default: boolean;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

interface EditOffer {
  offer_id?: number;
  store_id?: number;
  title: string;
  is_default: boolean;
  valid_from: string;
  valid_until: string;
  offer_image?: string;
  offer_image_url?: string;
}

interface Store {
  store_id: number;
  store_name: string;
}

const EMPTY_OFFER: EditOffer = {
  title: "",
  is_default: false,
  valid_from: "",
  valid_until: "",
};

const OffersPage = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedOffers, setSelectedOffers] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editOffer, setEditOffer] = useState<EditOffer>(EMPTY_OFFER);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchOffers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/offer?${params.toString()}`, { method: 'GET' }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setOffers(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        console.error("Failed to fetch offers. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const fetchStores = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/store?page=1&limit=100`, { method: 'GET' }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setStores(Array.isArray(data?.data) ? data.data : []);
      } else {
        console.error("Failed to fetch stores. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  }, [cookies.auth]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleAdd = () => {
    setEditOffer(EMPTY_OFFER);
    setImageFile(null);
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedOffers.length === 0) {
      alert("Please select at least one offer to update");
      return;
    }

    const offerToEdit = offers.find(offer => offer.offer_id === selectedOffers[0]);
    if (offerToEdit) {
      setEditOffer({
        offer_id: offerToEdit.offer_id,
        store_id: offerToEdit.store_id,
        title: offerToEdit.title || "",
        is_default: offerToEdit.is_default,
        valid_from: offerToEdit.valid_from ? offerToEdit.valid_from.split('T')[0] : "",
        valid_until: offerToEdit.valid_until ? offerToEdit.valid_until.split('T')[0] : "",
        offer_image: offerToEdit.offer_image,
        offer_image_url: offerToEdit.offer_image_url,
      });
      setImageFile(null);
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedOffers.length === 0) {
      alert("Please select at least one offer to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const offerId of selectedOffers) {
        const response = await authenticatedFetch(`${API_BASE}/offer/${offerId}`, { method: "DELETE" }, cookies.auth);
        if (!response.ok) {
          console.error(`Failed to delete offer ${offerId}. Status:`, response.status);
        }
      }
      setSelectedOffers([]);
      setShowDeleteConfirm(false);
      await fetchOffers();
    } catch (error) {
      console.error("Error deleting offers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);

      if (!editOffer.store_id) {
        snack.onResponse({ message: "Please select a store", status: 400 });
        return;
      }
      if (!editOffer.is_default) {
        if (!editOffer.valid_from || !editOffer.valid_until) {
          snack.onResponse({ message: "Valid from and valid until dates are required unless this is the default offer", status: 400 });
          return;
        }
        if (new Date(editOffer.valid_from) >= new Date(editOffer.valid_until)) {
          snack.onResponse({ message: "Valid until date must be after valid from date", status: 400 });
          return;
        }
      }
      if (!isEdit && !imageFile) {
        snack.onResponse({ message: "An offer image is required", status: 400 });
        return;
      }

      let imageBase64: string | null = null;
      if (imageFile) {
        imageBase64 = await convertFileToBase64(imageFile);
      }

      const payload: Record<string, unknown> = {
        store_id: editOffer.store_id,
        title: editOffer.title.trim(),
        is_default: editOffer.is_default,
        valid_from: editOffer.is_default ? (editOffer.valid_from || null) : editOffer.valid_from,
        valid_until: editOffer.is_default ? (editOffer.valid_until || null) : editOffer.valid_until,
      };
      if (imageBase64) {
        payload.offer_image = imageBase64;
      }

      const url = isEdit ? `${API_BASE}/offer/${editOffer.offer_id}` : `${API_BASE}/offer`;
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }, cookies.auth);

      if (response.ok) {
        setShowEditModal(false);
        setShowAddModal(false);
        setEditOffer(EMPTY_OFFER);
        setImageFile(null);
        setSelectedOffers([]);
        await fetchOffers();
        snack.onResponse({ message: "Offer saved successfully!", status: 200 });
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to save offer"), status: response.status });
      }
    } catch (error) {
      console.error("Error saving offer:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to save offer", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedOffers(checked ? offers.map(offer => offer.offer_id) : []);
  };

  const handleSelectOffer = (offerId: number, checked: boolean) => {
    setSelectedOffers(checked ? [offerId] : selectedOffers.filter(id => id !== offerId));
  };

  const filteredOffers = offers;

  const getStoreName = (storeId: number | null | undefined) => {
    if (!storeId) return "Unknown";
    const store = stores.find(s => s.store_id === storeId);
    return store ? store.store_name : "Unknown";
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  const renderForm = () => (
    <>
      <div className={style.formGroup}>
        <label>Store:</label>
        <select
          value={editOffer.store_id || ''}
          onChange={(e) => setEditOffer({...editOffer, store_id: parseInt(e.target.value)})}
          className={style.select}
        >
          <option value="">Select Store</option>
          {stores.map(store => (
            <option key={store.store_id} value={store.store_id}>{store.store_name}</option>
          ))}
        </select>
      </div>
      <div className={style.formGroup}>
        <label>Title (optional):</label>
        <input
          type="text"
          value={editOffer.title}
          onChange={(e) => setEditOffer({...editOffer, title: e.target.value})}
          className={style.input}
          placeholder="e.g. Free Delivery on Orders Above Rs. 5000"
        />
      </div>
      <div className={style.formGroup}>
        <label className={style.checkboxLabel}>
          <input
            type="checkbox"
            checked={editOffer.is_default}
            onChange={(e) => setEditOffer({...editOffer, is_default: e.target.checked})}
          />
          Default offer (always shown; only one per store)
        </label>
      </div>
      {!editOffer.is_default && (
        <>
          <div className={style.formGroup}>
            <label>Valid From:</label>
            <input
              type="date"
              value={editOffer.valid_from}
              onChange={(e) => setEditOffer({...editOffer, valid_from: e.target.value})}
              className={style.input}
            />
          </div>
          <div className={style.formGroup}>
            <label>Valid Until:</label>
            <input
              type="date"
              value={editOffer.valid_until}
              onChange={(e) => setEditOffer({...editOffer, valid_until: e.target.value})}
              className={style.input}
              min={editOffer.valid_from || undefined}
            />
          </div>
        </>
      )}
      <div className={style.formGroup}>
        <label>Offer Image:</label>
        <input type="file" onChange={handleImageChange} className={style.input} accept="image/*" />
        {editOffer.offer_image && !imageFile && (
          <img
            src={resolveImageUrl(editOffer.offer_image_url)}
            alt="Current offer"
            className={style.currentImage}
          />
        )}
      </div>
    </>
  );

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Offers</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by offer title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>Add</button>
              <button className={style.actionBtn} onClick={handleUpdate}>Update</button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>Delete</button>
            </div>
          )}
          {permissions.isSales && (
            <div className={style.viewOnlyNotice}>
              <span style={{ color: '#666', fontStyle: 'italic' }}>View-only access - Sales Person</span>
            </div>
          )}
        </div>
      </div>

      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedOffers.length === offers.length && offers.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Image</th>
              <th>Store</th>
              <th>Title</th>
              <th>Dates</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {filteredOffers.map((offer) => (
              <tr key={offer.offer_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedOffers.includes(offer.offer_id)}
                      onChange={(e) => handleSelectOffer(offer.offer_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>
                  <img src={resolveImageUrl(offer.offer_image_url)} alt={offer.title || 'Offer'} className={style.thumbnail} />
                </td>
                <td>{getStoreName(offer.store_id)}</td>
                <td>{offer.title || 'N/A'}</td>
                <td>
                  {offer.is_default ? (
                    <span className={style.defaultBadge}>Default</span>
                  ) : (
                    `${formatDate(offer.valid_from)} — ${formatDate(offer.valid_until)}`
                  )}
                </td>
                <td>{formatDate(offer.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {showAddModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Add Offer</h2>
            {renderForm()}
            <div className={style.modalActions}>
              <button onClick={() => setShowAddModal(false)} className={style.cancelBtn}>Cancel</button>
              <button onClick={() => handleSave(false)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Edit Offer</h2>
            {renderForm()}
            <div className={style.modalActions}>
              <button onClick={() => setShowEditModal(false)} className={style.cancelBtn}>Cancel</button>
              <button onClick={() => handleSave(true)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the selected offers?</p>
            <div className={style.modalActions}>
              <button onClick={() => setShowDeleteConfirm(false)} className={style.cancelBtn}>Cancel</button>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersPage;
